import { useEffect } from "react";
import { useAtomValue } from "jotai";
import { atomWithStorage } from "jotai/utils";

import { isMobileSync } from "@/core/hooks/useIsMobile";

/**
 * PerfMode — interrupteur global « réduire les effets coûteux » (blur,
 * animations lourdes) pour les téléphones / appareils peu puissants.
 *
 * C'est LE parapluie auquel les chantiers perf rattachent leur gating : blur
 * des en-têtes (cf `.panel-header-blur` dans `globals.css`), `layout`
 * framer-motion sur les cards, autoplay vidéo en grille, etc. Les sessions
 * qui ne le consomment pas encore gardent `isMobileSync()` en attendant — les
 * deux convergent par défaut (perfMode est ON par défaut sur vrai mobile).
 *
 * Défaut : ON si `isMobileSync()` (vrai mobile détecté au boot, cf
 * `core/hooks/useIsMobile.ts`). Persisté en localStorage → le toggle Réglages
 * survit aux reloads, et un user peut :
 *   - (a) FORCER le mode perf sur un appareil non détecté comme mobile
 *     (vieux laptop, tablette poussive) ;
 *   - (b) le DÉSACTIVER s'il préfère les effets sur son mobile.
 *
 * `getOnInit: true` → on lit le localStorage de façon synchrone dès l'init de
 * l'atom, pour que `usePerfMode()` renvoie tout de suite la valeur persistée
 * (pas de bascule au mount).
 */
export const PERF_MODE_STORAGE_KEY = "cockpit:perf-mode";

export const perfModeAtom = atomWithStorage<boolean>(
  PERF_MODE_STORAGE_KEY,
  isMobileSync(),
  undefined,
  { getOnInit: true },
);

/** Hook lecture : `true` si le mode performance est actif. */
export function usePerfMode(): boolean {
  return useAtomValue(perfModeAtom);
}

/**
 * Synchronise `perfModeAtom` → attribut `data-perf-mode="true"` sur <html>,
 * pour que les règles CSS (`.panel-header-blur` & co.) coupent les effets
 * lourds. À monter une seule fois, sous le `JotaiProvider` (cf AppProviders).
 *
 * Le `data-mobile="true"` posé au boot dans `main.tsx` couvre déjà les mobiles
 * auto-détectés AVANT le premier paint (pas de flash). Ce stamp-ci gère le cas
 * du toggle manuel : desktop qui force le mode, ou mobile qui le décoche.
 */
export function PerfModeStyleSync(): null {
  const perfMode = usePerfMode();
  useEffect(() => {
    const root = document.documentElement;
    if (perfMode) root.dataset.perfMode = "true";
    else delete root.dataset.perfMode;
  }, [perfMode]);
  return null;
}
